import {
  queryOptions,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { knowledgeBaseKeys } from "@/lib/api/knowledge-base";
import type { ForumSyncStatus } from "@discord-bldr/shared";

export interface ForumIngestionSource {
  id: number;
  applicationGuildId: string;
  discordChannelId: string;
  channelName: string;
  filterTagIds: string[] | null;
  filterTagNames: string[] | null;
  addedBy: string;
  lastSyncAt: string | null;
  lastSyncThreadCount: number | null;
  syncStatus: ForumSyncStatus;
  syncError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ForumSourceThread {
  id: number;
  title: string | null;
  messageCount: number;
  replyCount: number;
  authorUsername: string | null;
  appliedTagNames: string[];
}

export const forumIngestionKeys = {
  all: ["forum-ingestion"] as const,
  sources: (applicationGuildId: string) =>
    [...forumIngestionKeys.all, "sources", applicationGuildId] as const,
  sourceDocuments: (applicationGuildId: string, sourceId: number) =>
    [
      ...forumIngestionKeys.all,
      "documents",
      applicationGuildId,
      sourceId,
    ] as const,
};

export const forumSourcesQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: forumIngestionKeys.sources(applicationGuildId),
    queryFn: () =>
      apiClient
        .get<{
          data: ForumIngestionSource[];
        }>(`/api/guilds/${applicationGuildId}/forum-sources`)
        .then((r) => r.data),
    enabled: !!applicationGuildId,
  });

export function useForumSources(applicationGuildId: string) {
  return useQuery(forumSourcesQueryOptions(applicationGuildId));
}

export function useAddForumSource(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      discordChannelId: string;
      channelName: string;
      filterTagIds?: string[];
      filterTagNames?: string[];
    }) =>
      apiClient
        .post<{
          data: {
            source: ForumIngestionSource;
            jobId: string | null;
            status: string;
          };
        }>(`/api/guilds/${applicationGuildId}/forum-sources`, data)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: forumIngestionKeys.sources(applicationGuildId),
      });
    },
  });
}

export function useDeleteForumSource(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sourceId,
      deleteDocuments = false,
    }: {
      sourceId: number;
      deleteDocuments?: boolean;
    }) =>
      apiClient
        .delete<{
          data: { success: boolean; documentsDeleted: number };
        }>(
          `/api/guilds/${applicationGuildId}/forum-sources/${sourceId}?deleteDocuments=${deleteDocuments}`
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: forumIngestionKeys.sources(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.documents(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: knowledgeBaseKeys.status(applicationGuildId),
      });
    },
  });
}

export function useSyncForumSource(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sourceId: number) =>
      apiClient
        .post<{
          data: { jobId: string; status: string };
        }>(`/api/guilds/${applicationGuildId}/forum-sources/${sourceId}/sync`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: forumIngestionKeys.sources(applicationGuildId),
      });
    },
  });
}

export function useForumSourceDocuments(
  applicationGuildId: string,
  sourceId: number | null
) {
  return useQuery({
    queryKey: forumIngestionKeys.sourceDocuments(
      applicationGuildId,
      sourceId ?? 0
    ),
    queryFn: () =>
      apiClient
        .get<{
          data: ForumSourceThread[];
        }>(
          `/api/guilds/${applicationGuildId}/forum-sources/${sourceId}/documents`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId && sourceId !== null,
  });
}
